import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  Card,
  CardHeader,
  CardContent,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  TablePagination,
  CircularProgress,
  Alert,
  TextField,
  TableSortLabel,
  IconButton,
  InputAdornment,
} from '@mui/material';
import {
  CategoryScale,
  Chart as ChartJS,
  Tooltip as ChartJSTooltip,
  Filler,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  TimeScale,
  Title,
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import zoomPlugin from 'chartjs-plugin-zoom';
import { IGPUCardDistributionDetail, loadBalancerAPI } from '../../../../api/load-balancer';
import { formatNumber } from '../../../../abelcommon';
import { CenteredTableCell } from '../../../../abelcommon/components/CenteredTableCell';
import ClearIcon from '@mui/icons-material/Clear';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  ChartJSTooltip,
  Filler,
  Legend,
  TimeScale,
  zoomPlugin
);

interface GPUCardDistributionDialogProps {
  open: boolean;
  onClose: () => void;
  date: Date;
  apiError: any;
  apiLoading: boolean;
}

const sortData = (data: IGPUCardDistributionDetail[], order: string, orderBy: string) => {
  return data.slice().sort((a, b) => {
    if (orderBy === 'onlineNumber') {
      return order === 'asc' ? a.onlineNumber - b.onlineNumber : b.onlineNumber - a.onlineNumber;
    }
    if (orderBy === 'miningClient') {
      return order === 'asc'
        ? a.miningClient.localeCompare(b.miningClient)
        : b.miningClient.localeCompare(a.miningClient);
    }
    return 0;
  });
};

export const AllGPUCardDistributionDetailDialog = ({ open, onClose, date }: GPUCardDistributionDialogProps) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [orderBy, setOrderBy] = useState<'onlineNumber' | 'miningClient'>('onlineNumber');
  const [searchTerm, setSearchTerm] = useState('');

  const {
    data: gpuCardDistributionDetail,
    isLoading: gpuCardDistributionDetailLoading,
    error: gpuCardDistributionDetailsError,
  } = loadBalancerAPI.hookWrapperQuery.useGetGPUCardDistributionDetails(date);

  const filteredCardDistributionDetail = useMemo(() => {
    return gpuCardDistributionDetail
      ? gpuCardDistributionDetail.filter(
          (row) => row.cardName.toLowerCase().includes(searchTerm.toLowerCase()) && row.totalHashRate > 0
        )
      : null;
  }, [searchTerm, gpuCardDistributionDetail]);

  const sortedCardDistributionDetail = useMemo(() => {
    return filteredCardDistributionDetail ? sortData(filteredCardDistributionDetail, order, orderBy) : null;
  }, [order, orderBy, filteredCardDistributionDetail]);

  const paginatedCardDistributionDetail = useMemo(() => {
    return sortedCardDistributionDetail
      ? sortedCardDistributionDetail.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      : null;
  }, [page, rowsPerPage, sortedCardDistributionDetail]);

  useEffect(() => {
    setPage(0);
  }, [rowsPerPage, order, orderBy, searchTerm]);

  const handleRequestSort = (property: any) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <Card>
        <CardHeader title="GPU cards Distribution" />
        <CardContent sx={{ maxHeight: '90vh', overflow: 'auto' }}>
          <Typography
            variant="h6"
            color="text.primary"
            sx={{ marginBottom: '16px', fontWeight: 'bold', fontSize: '1rem' }}
          >
            {`Date : ${new Date(date).toLocaleDateString('en-GB')} ${new Date(date).toLocaleTimeString('en-GB')}`}
          </Typography>
          <TextField
            label="Search Card Name"
            variant="outlined"
            fullWidth
            margin="normal"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton onClick={handleClearSearch} edge="end">
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TableContainer sx={{ overflow: 'auto' }}>
            <Table sx={{ minWidth: '500px' }}>
              <TableHead>
                <TableRow>
                  <TableCell>Card</TableCell>
                  <TableCell
                    sortDirection={orderBy === 'onlineNumber' ? order : false}
                    onClick={() => handleRequestSort('onlineNumber')}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableSortLabel
                      active={orderBy === 'onlineNumber'}
                      direction={orderBy === 'onlineNumber' ? order : 'asc'}
                    >
                      Online Number
                    </TableSortLabel>
                  </TableCell>

                  <TableCell
                    sortDirection={orderBy === 'miningClient' ? order : false}
                    onClick={() => handleRequestSort('miningClient')}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableSortLabel
                      active={orderBy === 'miningClient'}
                      direction={orderBy === 'miningClient' ? order : 'asc'}
                    >
                      Mining Client
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Total Hash Rate (Mh/s)</TableCell>
                  <TableCell>Average Hash Rate (Mh/s)</TableCell>
                  <TableCell>Operating System</TableCell>
                  <TableCell>Token Name</TableCell>
                  <TableCell>Token Symbol</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {gpuCardDistributionDetailLoading ? (
                  <TableRow>
                    <CenteredTableCell colSpan={7}>
                      <CircularProgress />
                    </CenteredTableCell>
                  </TableRow>
                ) : gpuCardDistributionDetailsError ? (
                  <TableRow>
                    <CenteredTableCell colSpan={7}>
                      <Alert severity="error" sx={{ backgroundColor: 'transparent', p: 0, textAlign: 'center' }}>
                        {gpuCardDistributionDetailsError.message}
                      </Alert>
                    </CenteredTableCell>
                  </TableRow>
                ) : !paginatedCardDistributionDetail || paginatedCardDistributionDetail.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ textAlign: 'center' }}>
                      No Data
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedCardDistributionDetail.map((row, index) => (
                    <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                      <TableCell component="th" scope="row">
                        {row.cardName}
                      </TableCell>
                      <TableCell>{formatNumber(row.onlineNumber, { maximumFractionDigits: 2 })}</TableCell>
                      <TableCell>{row.miningClient}</TableCell>
                      <TableCell>{formatNumber(row.totalHashRate, { maximumFractionDigits: 2 })}</TableCell>
                      <TableCell>
                        {formatNumber(row.totalHashRate / row.onlineNumber, { maximumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell>{row.operatingSystem}</TableCell>
                      <TableCell>{row.tokenName}</TableCell>
                      <TableCell>{row.tokenSymbol}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            sx={{ pl: 0 }}
            rowsPerPageOptions={[50, 100, 200]}
            component="div"
            count={filteredCardDistributionDetail ? filteredCardDistributionDetail.length : 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => setRowsPerPage(+e.target.value)}
          />
        </CardContent>
      </Card>
    </Dialog>
  );
};
